// src/pages/admin/EmailTemplate.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Menu, 
  X, 
  Edit2, 
  Save, 
  Eye, 
  Code,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader,
  Copy,
  Check,
  RotateCcw,
  Plus,
  Send,
  ArrowRight
} from 'lucide-react';

const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://api.eventhouse.in';
// const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://styles-questions-ver-mat.trycloudflare.com';

const EmailTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [sidebarTemplates, setSidebarTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testContext, setTestContext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [viewMode, setViewMode] = useState('preview');
  const [editViewMode, setEditViewMode] = useState('edit');
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({
    label: '',
    value: '',
    icon: '',
    category: '',
    name: '',
    subject: '',
    content: '',
    variables: ''
  });
  const [testForm, setTestForm] = useState({
    receiverEmail: '',
    variables: {}
  });
  const [notification, setNotification] = useState(null);
  const [copied, setCopied] = useState(false);

  // Create action options map from sidebar templates
  const actionOptionsMap = sidebarTemplates.reduce((acc, option) => {
    acc[option.value] = {
      label: option.label,
      icon: option.icon,
      category: option.category
    };
    return acc;
  }, {});

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch initial data (templates and sidebar)
  const fetchInitialData = async () => {
    setIsFetching(true);
    try {
      await Promise.all([fetchTemplates(), fetchSidebarTemplates()]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      showNotification('error', 'Failed to load data');
    } finally {
      setIsFetching(false);
    }
  };

  // Fetch all templates from backend
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/v1/admin/email/all`, {
        withCredentials: true,
        headers: {
          "authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });
      
      if (response.data.success) {
        const fetchedTemplates = response.data.emails || [];
        setTemplates(fetchedTemplates);
        if (fetchedTemplates.length > 0 && !selectedTemplate) {
          setSelectedTemplate(fetchedTemplates[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      showNotification('error', 'Failed to fetch templates.');
      setTemplates([]);
    }
  };

  // Fetch sidebar templates
  const fetchSidebarTemplates = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/v1/admin/email/sidebar-template/all`, {
        withCredentials: true,
        headers: {
          "authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });
      
      if (response.data.success) {
        setSidebarTemplates(response.data.sidebarData || []);
      }
    } catch (error) {
      console.error('Error fetching sidebar templates:', error);
      setSidebarTemplates([]);
    }
  };

  // Open edit modal
  const openEditModal = (template) => {
    setOriginalTemplate(template);
    setEditForm({
      name: template.name || '',
      subject: template.subject || '',
      content: template.content || '',
      variables: Array.isArray(template.variables) 
        ? template.variables.join(', ') 
        : ''
    });
    setEditViewMode('edit');
    setIsEditModalOpen(true);
  };

  // Open create modal
  const openCreateModal = () => {
    setCreateForm({
      label: '',
      value: '',
      icon: '',
      category: '',
      name: '',
      subject: '',
      content: '',
      variables: ''
    });
    setCreateStep(1);
    setIsCreateModalOpen(true);
  };

  // Open test modal
  const openTestModal = (context, templateData) => {
    const vars = templateData?.variables || selectedTemplate?.variables || [];
    const variableObj = {};
    
    if (Array.isArray(vars)) {
      vars.forEach(v => {
        variableObj[v] = '';
      });
    } else if (typeof vars === 'string') {
      vars.split(',').map(v => v.trim()).filter(Boolean).forEach(v => {
        variableObj[v] = '';
      });
    }

    setTestForm({
      receiverEmail: '',
      variables: variableObj
    });
    setTestContext(context);
    setIsTestModalOpen(true);
  };

  // Reset form to original values
  const handleResetForm = () => {
    if (originalTemplate) {
      setEditForm({
        name: originalTemplate.name || '',
        subject: originalTemplate.subject || '',
        content: originalTemplate.content || '',
        variables: Array.isArray(originalTemplate.variables) 
          ? originalTemplate.variables.join(', ') 
          : ''
      });
      showNotification('info', 'Form reset to original values');
    }
  };

  // Replace placeholders for preview
  const replaceTemplateVariables = (content, variables) => {
    let result = content;
    Object.keys(variables).forEach(key => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
      result = result.replace(regex, variables[key] || `{{${key}}}`);
    });
    return result;
  };

  // Get preview content during edit
  const getEditPreviewContent = () => {
    const variableObj = {};
    if (editForm.variables) {
      const vars = editForm.variables.split(',').map(v => v.trim()).filter(Boolean);
      vars.forEach(v => {
        variableObj[v] = `[${v}]`;
      });
    }
    return replaceTemplateVariables(editForm.content, variableObj);
  };

  // Get preview content during create
  const getCreatePreviewContent = () => {
    const variableObj = {};
    if (createForm.variables) {
      const vars = createForm.variables.split(',').map(v => v.trim()).filter(Boolean);
      vars.forEach(v => {
        variableObj[v] = `[${v}]`;
      });
    }
    return replaceTemplateVariables(createForm.content, variableObj);
  };

  // Create new template - Step 1: Create sidebar data
  const handleCreateSidebarData = async () => {
    setIsLoading(true);
    try {
      const sidebarData = {
        label: createForm.label,
        value: createForm.value,
        icon: createForm.icon,
        category: createForm.category
      };

      const response = await axios.post(
        `${baseURL}/api/v1/admin/email/sidebar-template/create`,
        sidebarData,
        { 
          withCredentials: true,
          headers: {
            "authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (response.data.success) {
        await fetchSidebarTemplates();
        setCreateStep(2);
        showNotification('success', 'Sidebar data created! Now create the template content.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to create sidebar data';
      showNotification('error', errorMsg);
      console.error('Create sidebar error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Create new template - Step 2: Create email template
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const createData = {
        action: createForm.value,
        name: createForm.name,
        subject: createForm.subject,
        content: createForm.content,
        variables: createForm.variables
          .split(',')
          .map(v => v.trim())
          .filter(Boolean),
        isActive: true
      };

      const response = await axios.post(
        `${baseURL}/api/v1/admin/email/create`,
        createData,
        { 
          withCredentials: true,
          headers: {
            "authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (response.data.success) {
        showNotification('success', 'Template created successfully!');
        setIsCreateModalOpen(false);
        setCreateStep(1);
        await fetchTemplates();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to create template';
      showNotification('error', errorMsg);
      console.error('Create error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update template
  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    
    if (!selectedTemplate?._id) {
      showNotification('error', 'No template selected');
      return;
    }

    setIsLoading(true);
    
    try {
      const updateData = {
        name: editForm.name,
        subject: editForm.subject,
        content: editForm.content,
        variables: editForm.variables
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
      };

      const response = await axios.put(
        `${baseURL}/api/v1/admin/email/${selectedTemplate._id}`, 
        updateData,
        { 
          withCredentials: true,
          headers: {
            "authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (response.data.success) {
        showNotification('success', 'Template updated successfully!');
        setIsEditModalOpen(false);
        await fetchTemplates();
        
        const updatedTemplate = response.data.updateEmail;
        setSelectedTemplate(updatedTemplate);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to update template';
      showNotification('error', errorMsg);
      console.error('Update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Send test email
  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const testData = {
        email: testForm.receiverEmail,
        variables: testForm.variables,
        content: testContext === 'create' ? createForm.content : selectedTemplate.content
      };

      const response = await axios.post(
        `${baseURL}/api/v1/admin/email/test-email`,
        testData,
        { 
          withCredentials: true,
          headers: {
            "authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (response.data.success) {
        showNotification('success', 'Test email sent successfully!');
        setIsTestModalOpen(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to send test email';
      showNotification('error', errorMsg);
      console.error('Test email error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    showNotification('success', 'Code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    const category = actionOptionsMap[template.action]?.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {});

  // Loading state
  if (isFetching) {
    return (
      <div className="flex h-[calc(100dvh-6rem)] items-center justify-center bg-gradient-to-br from-[#FFF4F0] to-white">
        <div className="text-center">
          <Loader className="animate-spin text-[#F4A696] mx-auto mb-4" size={48} />
          <p className="text-lg font-semibold text-[#1B3C5C]">Loading email templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-6.5rem)] bg-gradient-to-br from-[#FFF4F0] to-white overflow-hidden">
      {/* Sidebar */}
      <aside 
        className={`${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:relative z-10 w-80 h-full bg-white border-r border-[#F4A696]/20 transition-transform duration-300 ease-in-out flex flex-col shadow-lg`}
      >
        {/* Sidebar Header */}
        <div className="flex-shrink-0 rounded-tl-xl p-6 border-b border-[#F4A696]/20 bg-gradient-to-r from-[#F4A696] to-[#E89580]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <span className="text-2xl">📧</span>
                Email Templates
              </h2>
              <p className="text-white/90 text-sm mt-1">{templates.length} templates available</p>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden text-white hover:text-white/80 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <button
            onClick={openCreateModal}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-[#F4A696] rounded-lg hover:bg-white/90 transition-all font-semibold shadow-md hover:shadow-lg"
          >
            <Plus size={18} />
            Create New Template
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-[#F4A696] scrollbar-track-transparent">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No templates found</p>
              <p className="text-xs mt-2">Click "Create New Template" to get started</p>
            </div>
          ) : (
            Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
              <div key={category} className="mb-6">
                <h3 className="text-xs font-bold text-[#F4A696] uppercase tracking-wider mb-3 px-2 flex items-center gap-2">
                  <div className="w-8 h-0.5 bg-gradient-to-r from-[#F4A696] to-transparent"></div>
                  {category}
                </h3>
                <div className="space-y-2">
                  {categoryTemplates.map((template) => (
                    <button
                      key={template._id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all group relative overflow-hidden ${
                        selectedTemplate?._id === template._id
                          ? 'bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white shadow-lg'
                          : 'hover:bg-[#FFF4F0] text-gray-700 hover:shadow-md'
                      }`}
                    >
                      {selectedTemplate?._id === template._id && (
                        <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                      )}
                      <span className="text-2xl relative z-10">{actionOptionsMap[template.action]?.icon || '📧'}</span>
                      <div className="flex-1 text-left relative z-10">
                        <p className={`font-semibold text-sm ${
                          selectedTemplate?._id === template._id ? 'text-white' : 'text-gray-800'
                        }`}>
                          {template.name}
                        </p>
                        <p className={`text-xs mt-0.5 ${
                          selectedTemplate?._id === template._id ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          {template.action}
                        </p>
                      </div>
                      {selectedTemplate?._id === template._id && (
                        <ChevronRight size={18} className="text-white relative z-10" />
                      )}
                      <div className={`w-2 h-2 rounded-full relative z-10 ${
                        template.isActive ? 'bg-green-400 shadow-lg shadow-green-400/50' : 'bg-gray-300'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-white">
        {/* Top Bar */}
        <header className="flex-shrink-0 bg-white border-b border-[#F4A696]/20 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-[#F4A696] transition-colors"
              >
                <Menu size={24} />
              </button>
              
              {selectedTemplate && (
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-[#1B3C5C] flex items-center gap-2">
                    <span className="text-xl md:text-2xl">{actionOptionsMap[selectedTemplate.action]?.icon || '📧'}</span>
                    <span className="hidden sm:inline">{selectedTemplate.name}</span>
                  </h1>
                  <p className="text-xs md:text-sm text-gray-600 mt-1 hidden md:block">{selectedTemplate.description}</p>
                </div>
              )}
            </div>

            {selectedTemplate && (
              <div className="flex items-center gap-2 md:gap-3">
                {/* View Mode Toggle */}
                <div className="flex bg-[#FFF4F0] rounded-xl p-1 border border-[#F4A696]/20">
                  <button
                    onClick={() => setViewMode('preview')}
                    className={`px-3 md:px-5 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-semibold transition-all ${
                      viewMode === 'preview'
                        ? 'bg-white text-[#F4A696] shadow-md border border-[#F4A696]/20'
                        : 'text-gray-600 hover:text-[#F4A696]'
                    }`}
                  >
                    <Eye size={14} className="inline mr-1 md:mr-2" />
                    <span className="hidden sm:inline">Preview</span>
                  </button>
                  <button
                    onClick={() => setViewMode('code')}
                    className={`px-3 md:px-5 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-semibold transition-all ${
                      viewMode === 'code'
                        ? 'bg-white text-[#F4A696] shadow-md border border-[#F4A696]/20'
                        : 'text-gray-600 hover:text-[#F4A696]'
                    }`}
                  >
                    <Code size={14} className="inline mr-1 md:mr-2" />
                    <span className="hidden sm:inline">Code</span>
                  </button>
                </div>

                <button
                  onClick={() => openEditModal(selectedTemplate)}
                  className="flex items-center gap-1 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-xl hover:from-[#E89580] hover:to-[#F4A696] transition-all shadow-md hover:shadow-lg font-semibold text-xs md:text-sm"
                >
                  <Edit2 size={16} />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#FFF4F0] to-white">
          {!selectedTemplate ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-6 bg-gradient-to-br from-[#FFF4F0] to-[#F4A696]/20 rounded-full flex items-center justify-center">
                  <ChevronRight size={48} className="text-[#F4A696] md:w-16 md:h-16" />
                </div>
                <p className="text-lg md:text-xl font-bold text-[#1B3C5C]">Select a template to view</p>
                <p className="text-xs md:text-sm mt-2 text-gray-500">Choose from the sidebar to get started</p>
              </div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto">
              {/* Template Info Card */}
              <div className="bg-white rounded-2xl shadow-md border border-[#F4A696]/20 p-4 md:p-6 mb-4 md:mb-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
                  <div className="bg-gradient-to-br from-[#FFF4F0] to-white p-3 md:p-4 rounded-xl border border-[#F4A696]/20">
                    <p className="text-xs text-[#F4A696] font-semibold mb-2 uppercase tracking-wide">Action Key</p>
                    <p className="font-mono text-xs md:text-sm bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-[#1B3C5C] font-bold shadow-sm border border-[#F4A696]/10 break-all">
                      {selectedTemplate.action}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-white p-3 md:p-4 rounded-xl border border-blue-200/20">
                    <p className="text-xs text-blue-600 font-semibold mb-2 uppercase tracking-wide">Category</p>
                    <p className="font-bold text-[#1B3C5C] capitalize text-sm md:text-base">{actionOptionsMap[selectedTemplate.action]?.category || 'N/A'}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-white p-3 md:p-4 rounded-xl border border-green-200/20">
                    <p className="text-xs text-green-600 font-semibold mb-2 uppercase tracking-wide">Status</p>
                    <span className={`inline-flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-bold ${
                      selectedTemplate.isActive
                        ? 'bg-green-500 text-white shadow-md'
                        : 'bg-gray-300 text-gray-700'
                    }`}>
                      <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${
                        selectedTemplate.isActive ? 'bg-white animate-pulse' : 'bg-gray-500'
                      }`} />
                      {selectedTemplate.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-white p-3 md:p-4 rounded-xl border border-orange-200/20">
                    <p className="text-xs text-orange-600 font-semibold mb-2 uppercase tracking-wide">Variables</p>
                    <p className="font-bold text-[#1B3C5C] text-sm md:text-base">{selectedTemplate.variables?.length || 0} defined</p>
                  </div>
                </div>

                {/* Subject */}
                <div className="mt-4 pt-4 border-t border-[#F4A696]/20">
                  <p className="text-xs md:text-sm text-[#F4A696] font-semibold mb-2 uppercase tracking-wide">Email Subject</p>
                  <p className="font-semibold text-sm md:text-base text-[#1B3C5C] bg-gradient-to-r from-[#FFF4F0] to-white px-3 md:px-4 py-2 md:py-3 rounded-lg border border-[#F4A696]/10 break-words">
                    {selectedTemplate.subject}
                  </p>
                </div>

                {/* Variables */}
                {selectedTemplate.variables?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#F4A696]/20">
                    <p className="text-xs md:text-sm text-[#F4A696] font-semibold mb-3 uppercase tracking-wide">Available Variables</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map((variable, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs font-mono bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white font-bold shadow-md hover:shadow-lg transition-all cursor-default"
                        >
                          {`{{${variable}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Template Content */}
              <div className="bg-white rounded-2xl shadow-md border border-[#F4A696]/20 overflow-hidden">
                <div className="border-b border-[#F4A696]/20 px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-[#FFF4F0] to-white flex items-center justify-between">
                  <h3 className="font-bold text-[#F4A696] flex items-center gap-2 text-sm md:text-base">
                    {viewMode === 'preview' ? (
                      <>
                        <Eye size={18} />
                        <span className="hidden sm:inline">Email Preview</span>
                      </>
                    ) : (
                      <>
                        <Code size={18} />
                        <span className="hidden sm:inline">HTML Source Code</span>
                      </>
                    )}
                  </h3>
                  {viewMode === 'code' && (
                    <button
                      onClick={() => copyToClipboard(selectedTemplate.content)}
                      className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-[#F4A696] hover:bg-[#E89580] text-white text-xs md:text-sm rounded-lg transition-all shadow-md hover:shadow-lg font-semibold"
                    >
                      {copied ? (
                        <>
                          <Check size={14} />
                          <span className="hidden sm:inline">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          <span className="hidden sm:inline">Copy</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                <div className="p-3 md:p-6">
                  {viewMode === 'preview' ? (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 md:p-6 border border-[#F4A696]/10">
                      <div 
                        className="bg-white max-w-2xl mx-auto shadow-xl rounded-lg overflow-hidden border border-gray-200"
                        dangerouslySetInnerHTML={{ __html: selectedTemplate.content }}
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <pre className="bg-[#1B3C5C] text-gray-100 p-3 md:p-6 rounded-xl overflow-x-auto text-xs leading-relaxed shadow-xl border border-[#1B3C5C] max-h-[500px] md:max-h-[600px]">
                        <code className="language-html whitespace-pre-wrap break-words">{selectedTemplate.content}</code>
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create Template Modal - Two Step */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`bg-white rounded-2xl shadow-2xl ${createStep === 1 ? 'max-w-2xl' : 'max-w-7xl'} w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-[#F4A696]/20`}>
            <div className="px-6 py-5 border-b border-[#F4A696]/20 flex items-center justify-between bg-gradient-to-r from-[#F4A696] to-[#E89580]">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Plus size={24} />
                  Create New Email Template
                </h2>
                <p className="text-white/80 text-sm mt-1">Step {createStep} of 2</p>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-white hover:text-white/80 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {createStep === 1 ? (
                // Step 1: Sidebar Data
                <div className="max-w-2xl mx-auto">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>Step 1:</strong> Define the template metadata (label, value, icon, category)
                    </p>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Label (Display Name) *</label>
                      <input
                        type="text"
                        value={createForm.label}
                        onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium"
                        required
                        placeholder="e.g., Payment Success"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Value (Action Key) *</label>
                      <input
                        type="text"
                        value={createForm.value}
                        onChange={(e) => setCreateForm({ ...createForm, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-mono text-sm"
                        required
                        placeholder="e.g., payment_success"
                      />
                      <p className="text-xs text-gray-500 mt-2">Use lowercase with underscores (e.g., payment_success)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Icon *</label>
                      <input
                        type="text"
                        value={createForm.icon}
                        onChange={(e) => setCreateForm({ ...createForm, icon: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium text-2xl"
                        required
                        placeholder="Press Windows + . to choose emoji"
                      />
                      <p className="text-xs text-gray-500 mt-2 bg-[#FFF4F0] px-3 py-2 rounded-lg">
                        💡 Press <code className="bg-white px-2 py-1 rounded text-[#F4A696] font-mono border border-[#F4A696]/20">Windows + .</code> or <code className="bg-white px-2 py-1 rounded text-[#F4A696] font-mono border border-[#F4A696]/20">Cmd + Ctrl + Space</code> to open emoji picker
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Category *</label>
                      <input
                        type="text"
                        value={createForm.category}
                        onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium"
                        required
                        placeholder="e.g., authentication, payment, booking"
                      />
                      <p className="text-xs text-gray-500 mt-2">Common categories: authentication, payment, booking, notification, system</p>
                    </div>
                  </div>
                </div>
              ) : (
                // Step 2: Template Content with Preview
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left: Edit Form */}
                  <div className="space-y-5">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <p className="text-sm text-green-800">
                        <strong>Step 2:</strong> Create the email template content
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Template Name *</label>
                      <input
                        type="text"
                        value={createForm.name}
                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium"
                        required
                        placeholder="e.g., Payment Success Email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Email Subject *</label>
                      <input
                        type="text"
                        value={createForm.subject}
                        onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium"
                        required
                        placeholder="e.g., Payment Successful - {{eventName}}"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Variables (comma separated)</label>
                      <input
                        type="text"
                        value={createForm.variables}
                        onChange={(e) => setCreateForm({ ...createForm, variables: e.target.value })}
                        placeholder="name, email, amount"
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-mono text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">HTML Content *</label>
                      <textarea
                        value={createForm.content}
                        onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })}
                        rows={16}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] font-mono text-sm transition-all"
                        required
                        placeholder="<html>...</html>"
                      />
                    </div>
                  </div>

                  {/* Right: Preview */}
                  <div className="space-y-4">
                    <div className="sticky top-0 space-y-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <p className="text-sm text-purple-800 font-semibold flex items-center gap-2">
                          <Eye size={16} />
                          Live Preview
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-[#F4A696]/10 max-h-[600px] overflow-y-auto">
                        <div className="mb-4">
                          <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Subject Preview:</label>
                          <div className="mt-2 bg-white p-3 rounded-lg border border-gray-200 font-semibold text-gray-800 text-sm">
                            {createForm.subject || 'No subject yet...'}
                          </div>
                        </div>
                        
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Content Preview:</label>
                        {createForm.content ? (
                          <div 
                            className="mt-2 bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200"
                            dangerouslySetInnerHTML={{ __html: getCreatePreviewContent() }}
                          />
                        ) : (
                          <div className="mt-2 bg-white p-8 rounded-lg border border-gray-200 text-center text-gray-400">
                            Start typing HTML content to see preview...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#F4A696]/20 bg-gradient-to-r from-[#FFF4F0] to-white flex items-center justify-between gap-3">
              <div>
                {createStep === 2 && (
                  <button
                    type="button"
                    onClick={() => setCreateStep(1)}
                    className="flex items-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all font-bold border-2 border-gray-300"
                  >
                    ← Back
                  </button>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-6 py-3 border-2 border-[#F4A696]/30 rounded-xl hover:bg-[#FFF4F0] transition-all font-bold text-[#1B3C5C]"
                >
                  Cancel
                </button>

                {createStep === 2 && (
                  <button
                    type="button"
                    onClick={() => openTestModal('create', { variables: createForm.variables })}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all font-bold shadow-md hover:shadow-lg"
                  >
                    <Send size={18} />
                    Test Email
                  </button>
                )}

                {createStep === 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!createForm.label || !createForm.value || !createForm.icon || !createForm.category) {
                        showNotification('error', 'Please fill all required fields');
                        return;
                      }
                      handleCreateSidebarData();
                    }}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-xl hover:from-[#E89580] hover:to-[#F4A696] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-md hover:shadow-lg"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        Creating...
                      </>
                    ) : (
                      <>
                        Next
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleCreateTemplate}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-xl hover:from-[#E89580] hover:to-[#F4A696] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-md hover:shadow-lg"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Create Template
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal with Preview */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-[#F4A696]/20">
            <div className="px-6 py-5 border-b border-[#F4A696]/20 flex items-center justify-between bg-gradient-to-r from-[#F4A696] to-[#E89580]">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit2 size={24} />
                Edit Email Template
              </h2>
              <div className="flex items-center gap-3">
                {/* Edit/Preview Toggle */}
                <div className="flex bg-white/20 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setEditViewMode('edit')}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                      editViewMode === 'edit'
                        ? 'bg-white text-[#F4A696] shadow-md'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <Code size={16} className="inline mr-2" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditViewMode('preview')}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                      editViewMode === 'preview'
                        ? 'bg-white text-[#F4A696] shadow-md'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <Eye size={16} className="inline mr-2" />
                    Preview
                  </button>
                </div>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {editViewMode === 'edit' ? (
                <form onSubmit={handleUpdateTemplate}>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Template Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Email Subject</label>
                      <input
                        type="text"
                        value={editForm.subject}
                        onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-medium"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-2 bg-[#FFF4F0] px-3 py-2 rounded-lg">
                        💡 Use <code className="bg-white px-2 py-1 rounded text-[#F4A696] font-mono border border-[#F4A696]/20">{`{{variableName}}`}</code> for dynamic content
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Variables (comma separated)</label>
                      <input
                        type="text"
                        value={editForm.variables}
                        onChange={(e) => setEditForm({ ...editForm, variables: e.target.value })}
                        placeholder="name, email, otp"
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] transition-all font-mono text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#1B3C5C] mb-2">HTML Content</label>
                      <textarea
                        value={editForm.content}
                        onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                        rows={16}
                        className="w-full px-4 py-3 border-2 border-[#F4A696]/20 rounded-xl focus:ring-2 focus:ring-[#F4A696] focus:border-[#F4A696] font-mono text-sm transition-all"
                        required
                      />
                    </div>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Preview Mode:</strong> Variables are displayed with placeholder values like <code className="bg-white px-2 py-1 rounded text-blue-600 font-mono">[variableName]</code>
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-[#F4A696]/10">
                    <div className="mb-4">
                      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Subject Preview:</label>
                      <div className="mt-2 bg-white p-3 rounded-lg border border-gray-200 font-semibold text-gray-800">
                        {editForm.subject}
                      </div>
                    </div>
                    
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Email Content Preview:</label>
                    <div 
                      className="mt-2 bg-white max-w-3xl mx-auto shadow-xl rounded-lg overflow-hidden border border-gray-200"
                      dangerouslySetInnerHTML={{ __html: getEditPreviewContent() }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#F4A696]/20 bg-gradient-to-r from-[#FFF4F0] to-white flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleResetForm}
                className="flex items-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all font-bold border-2 border-gray-300"
              >
                <RotateCcw size={18} />
                Reset
              </button>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-6 py-3 border-2 border-[#F4A696]/30 rounded-xl hover:bg-[#FFF4F0] transition-all font-bold text-[#1B3C5C]"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => openTestModal('edit', { variables: editForm.variables })}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all font-bold shadow-md hover:shadow-lg"
                >
                  <Send size={18} />
                  Test Email
                </button>

                <button
                  onClick={handleUpdateTemplate}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-xl hover:from-[#E89580] hover:to-[#F4A696] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-md hover:shadow-lg"
                >
                  {isLoading ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {isTestModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-[#F4A696]/20">
            <div className="px-6 py-5 border-b border-[#F4A696]/20 flex items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Send size={24} />
                Send Test Email
              </h2>
              <button
                onClick={() => setIsTestModalOpen(false)}
                className="text-white hover:text-white/80 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSendTestEmail} className="flex-1 overflow-y-auto p-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-[#1B3C5C] mb-2">Receiver Email *</label>
                  <input
                    type="email"
                    value={testForm.receiverEmail}
                    onChange={(e) => setTestForm({ ...testForm, receiverEmail: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                    required
                    placeholder="test@example.com"
                  />
                </div>

                {Object.keys(testForm.variables).length > 0 && (
                  <div>
                    <label className="block text-sm font-bold text-[#1B3C5C] mb-3">Template Variables</label>
                    <div className="space-y-3">
                      {Object.keys(testForm.variables).map((variable) => (
                        <div key={variable}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            {variable}
                          </label>
                          <input
                            type="text"
                            value={testForm.variables[variable] || ''}
                            onChange={(e) => setTestForm({
                              ...testForm,
                              variables: {
                                ...testForm.variables,
                                [variable]: e.target.value
                              }
                            })}
                            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono text-sm"
                            placeholder={`Enter ${variable}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This will send a test email using the {testContext === 'create' ? 'current' : 'selected'} template with the variables you provide above.
                  </p>
                </div>
              </div>
            </form>

            <div className="px-6 py-4 border-t border-blue-200 bg-gradient-to-r from-blue-50 to-white flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsTestModalOpen(false)}
                className="px-6 py-3 border-2 border-blue-300 rounded-xl hover:bg-blue-50 transition-all font-bold text-[#1B3C5C]"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-md hover:shadow-lg"
              >
                {isLoading ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Test Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-[60]">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border-2 ${
            notification.type === 'success'
              ? 'bg-white border-green-300 text-green-800'
              : notification.type === 'info'
              ? 'bg-white border-blue-300 text-blue-800'
              : 'bg-white border-red-300 text-red-800'
          }`}>
            {notification.type === 'success' ? (
              <CheckCircle size={24} className="text-green-600" />
            ) : (
              <AlertCircle size={24} className={notification.type === 'info' ? 'text-blue-600' : 'text-red-600'} />
            )}
            <p className="font-bold">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[5] lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export { EmailTemplateManager };
